import * as Sentry from '@sentry/browser'
import React, { useState, useEffect, FormEvent, KeyboardEvent } from 'react'
import { flowRight as compose, isEmpty } from 'lodash'
import { useHistory, useParams } from 'react-router-dom'
import { FormProvider, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'

import { schemaForm, initialForm, formatForm } from 'features/Product'
import { ProductForm, WithProduct } from 'features/Product'
import { Button, PromptGuard, Modal } from 'components'
import { ModalSuccess, Confirmation } from 'components/Modal'
import { create, renderBreadcrumbsCreate } from 'features/Product'
import { withBackOfficeCms } from 'helpers/hocs'
import {
  useBreadcrumbs,
  useFormSubmitLoader,
  usePromptForm,
} from 'helpers/hooks'
import { useTranslation } from 'react-i18next'
import { useSetRecoilState } from 'recoil'
import { loaderState } from 'recoils/atoms'

import type { ProductForForm, ProductForApiSubmit } from 'types'

const ProductCreate: React.VFC = () => {
  const base = 'f-product-form'

  const { t } = useTranslation(['layout', 'common', 'product'])

  const history = useHistory()

  const { branchId } = useParams<{ branchId: string }>()
  const methods = useForm<ProductForForm>({
    defaultValues: initialForm,
    resolver: yupResolver(schemaForm),
    mode: 'onBlur',
    reValidateMode: 'onBlur',
  })

  const [shouldPrompt, setShouldPrompt] = usePromptForm<ProductForForm>(methods)
  const [loading] = useFormSubmitLoader<ProductForForm>(methods)
  const [isOpenConfirm, setIsOpenConfirm] = useState<boolean>(false)
  const [isOpenSuccess, setIsOpenSuccess] = useState<boolean>(false)
  const [payload, setPayload] = useState<ProductForApiSubmit>()
  const setLoader = useSetRecoilState(loaderState)

  useEffect(() => {
    if (shouldPrompt || methods.formState.isSubmitted) {
      return
    }

    if (methods.formState.isDirty) {
      setShouldPrompt(true)
    }
  }, [methods.formState])

  useBreadcrumbs(renderBreadcrumbsCreate(branchId))

  const beforeSubmit = (e: FormEvent<HTMLFormElement>) => {
    methods.setValue('currentState', 'onSubmit')
    methods.handleSubmit(setData)(e)
    methods.setValue('currentState', '')
  }

  const callApi = async () => {
    if (payload) {
      try {
        setLoader(true)

        await create(payload, handleOpenSuccess)
        setLoader(false)
      } catch (error) {
        Sentry.captureException(error)
        setLoader(false)
      }
    }
  }

  const handleOpenSuccess = async () => {
    setIsOpenSuccess(true)

    await setTimeout(() => {
      setShouldPrompt(false)
      handleBack()
    }, 2000)
  }

  const handleBack = (): void => {
    history.push(`/branch/${branchId}/product`)
  }

  const setData = (data: ProductForForm) => {
    const payload = formatForm(branchId, data)

    if (isEmpty(methods.errors) && payload) {
      setIsOpenConfirm(true)
      setPayload(payload)
    }
  }

  const renderConfirmModal = () => {
    return (
      isOpenConfirm && (
        <Modal
          isIcon={false}
          isOpen={isOpenConfirm}
          onClose={() => setIsOpenConfirm(false)}
        >
          <Confirmation
            title={t('common:confirmation.title.label')}
            description={t('common:confirmation.description.label')}
            confirmText={t('common:save.button')}
            cancelText={t('common:cancel.button')}
            onConfirm={callApi}
            onClose={() => setIsOpenConfirm(false)}
          />
        </Modal>
      )
    )
  }

  const renderSuccessModal = () => {
    return (
      isOpenSuccess && (
        <ModalSuccess
          isOpen={isOpenSuccess}
          description={t('common:success.label')}
          onClose={() => setIsOpenSuccess(false)}
        />
      )
    )
  }

  const checkKeyDown = (e: KeyboardEvent<HTMLFormElement>) => {
    if (e.key === 'Enter') e.preventDefault()
  }

  return (
    <FormProvider {...methods}>
      <PromptGuard when={shouldPrompt} />
      <form
        className={base}
        onSubmit={beforeSubmit}
        onKeyDown={(e) => checkKeyDown(e)}
      >
        <ProductForm />
        <div className={`${base}__con-bottom`}>
          <Button variants='secondary' onClick={handleBack}>
            {t('common:cancel.button')}
          </Button>
          <Button type='submit' isLoading={loading}>
            {t('product:create.button')}
          </Button>
        </div>
      </form>

      {renderConfirmModal()}
      {renderSuccessModal()}
    </FormProvider>
  )
}

export default compose(withBackOfficeCms, WithProduct)(ProductCreate)
